import React, { useEffect, useMemo, useRef, useState } from "react";
import { Switch, useHistory, useParams } from "react-router-dom";
import PrivateRoute from "@common/components/route/PrivateRoute";
import { Typography, Box } from "@curves/ui-base";
import { BlankFormBox, SearchBar } from "@icoach/documents/components/";
import { ConfirmTemporaryContractDialog } from "@icoach/documents/dialog";
import MembershipQuitHistory from "@icoach/documents/membership/quit/MembershipQuitHistory";
import useDocMembershipQuitApi from "@apis/useDocMembershipQuitApi";
import { PEOPLE_TYPE } from "@icoach/constants/dataEnums";
import { DOC_MEMBERSHIP_QUIT_LINK } from "@icoach/documents/membership/staticData";
import { CLUB_DOCUMENTS_ROUTER } from "@icoach/router/routerPath";
import useToggleDialog from "@util/hook/useToggleDialog";
import { isEqual } from "@util/utils";
import MembershipQuitRenderer from "@icoach/documents/membership/quit/MembershipQuitRenderer";

const dialogTypeMap = {
	temporary: "temporary",
};

const MembershipQuitPage = () => {
	const history = useHistory();
	const { peopleType: peopleTypeProps, referenceID: referenceIDProps } = useParams();
	const { getDocMembershipQuitOptionApi, getDocMembershipQuitTemporaryApi, deleteDocMembershipQuitTemporaryApi } = useDocMembershipQuitApi();
	const { openTarget, handleOpenDialog, handleCloseDialog } = useToggleDialog();
	const [isMounted, setMounted] = useState(false);
	const [options, setOptions] = useState({});
	const searchRef = useRef([]);
	const temporaryIDRef = useRef(0);

	const goToContractPage = props => {
		const { peopleType = peopleTypeProps, referenceID = referenceIDProps, contractID } = props;
		const path = CLUB_DOCUMENTS_ROUTER.QUIT.GET_CONTRACT_PATH(peopleType, referenceID, contractID);
		history.push(path);
	};

	const checkPeopleType = opts => {
		const { targetOptions = [] } = opts;
		const isInclude = targetOptions.find(({ value }) => String(peopleTypeProps) === String(value));
		const defaultPeopleType = PEOPLE_TYPE.MEMBER;
		if (!peopleTypeProps || !isInclude) {
			const path = CLUB_DOCUMENTS_ROUTER.QUIT.GET_HISTORY_ONLY_PEOPLE_PATH(defaultPeopleType);
			history.replace(path);
		}
	};

	// 重設 按鈕
	const handleResetClick = type => {
		const path = CLUB_DOCUMENTS_ROUTER.QUIT.GET_HISTORY_ONLY_PEOPLE_PATH(type);
		history.replace(path);
	};

	// 搜尋 按鈕
	const handleSearchHistoryClick = id => {
		const { targetID, targetType } = searchRef.current.getResult();
		const path = CLUB_DOCUMENTS_ROUTER.QUIT.GET_HISTORY_PATH(targetType, id || targetID);
		history.push(path);
	};

	// 新增 按鈕
	const handleCreateClick = () => {
		const { targetID: id, targetType: type } = searchRef.current.getResult();
		const params = { id, type };
		doCreateBlankContractApi(params, type, id);
	};

	// 彈出視窗 要刪除暫存資料
	const handleDialogNoClick = () => {
		const { targetID, targetType } = searchRef.current.getResult();
		doDeleteTemporaryDataApi(targetType, targetID);
	};

	// 彈出視窗 讀取上次暫存資料繼續編輯
	const handleDialogYseClick = () => {
		if (!temporaryIDRef.current) return;
		handleCloseDialog();
		goToContractPage({ contractID: temporaryIDRef.current });
	};

	// 刪除暫存合約資料
	const doDeleteTemporaryDataApi = async (type, id) => {
		const resp = await deleteDocMembershipQuitTemporaryApi({ type, id });
		if (resp) {
			handleCloseDialog();
			goToContractPage({ contractID: 0 });
		}
	};

	// 新增空白頁面
	const doCreateBlankContractApi = async (params, type, id) => {
		let temporaryID;
		temporaryIDRef.current = 0;
		temporaryID = await getDocMembershipQuitTemporaryApi(params);
		if (temporaryID !== 0 && !!temporaryID) {
			temporaryIDRef.current = temporaryID;
			handleOpenDialog(dialogTypeMap.temporary);
		} else {
			goToContractPage({
				peopleType: type,
				referenceID: id,
				contractID: 0,
			});
		}
	};

	// 初始 search options
	const doInitOptionsApi = async () => {
		const resp = await getDocMembershipQuitOptionApi();
		if (resp) {
			checkPeopleType(resp);
			setOptions(resp);
		}
		setMounted(true);
	};

	const useSearchBarMemo = useMemo(
		() => {
			return (
				<SearchBar
					ref={searchRef}
					className="mb-4"
					sourceOption={options.targetOptions}
					onResetEvent={handleResetClick}
					onCreateEvent={handleCreateClick}
					onSearchHistoryEvent={handleSearchHistoryClick}
					routerPath={CLUB_DOCUMENTS_ROUTER.QUIT.BASE}
				/>
			);
		},
		// eslint-disable-next-line
		[options]
	);

	useEffect(
		() => {
			doInitOptionsApi();
			return () => setMounted(false);
		},
		// eslint-disable-next-line
		[]
	);

	if (!isMounted) return null;

	return (
		<Box className="container main-container-spacing">
			<Box component="header" className="page-header">
				<Typography className="page-header-title" variant="h3">
					會籍終止申請書
				</Typography>
				<Box className="btn-group">
					<BlankFormBox sourceData={DOC_MEMBERSHIP_QUIT_LINK} />
				</Box>
			</Box>
			{useSearchBarMemo}
			<Switch>
				<PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.QUIT.HISTORY} as={MembershipQuitHistory} onBlankContractClick={handleCreateClick} />
				<PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.QUIT.CONTRACT} as={MembershipQuitRenderer} onReturnHistory={handleSearchHistoryClick} />
				<PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.QUIT.READONLY_CONTRACT} as={MembershipQuitRenderer} isReadOnly={true} onReturnHistory={handleSearchHistoryClick} />
			</Switch>
			<ConfirmTemporaryContractDialog
				open={isEqual(openTarget, dialogTypeMap.temporary)}
				handleClose={handleCloseDialog}
				handleClickYes={handleDialogYseClick}
				handleClickNo={handleDialogNoClick}
			/>
		</Box>
	);
};

export default MembershipQuitPage;
